import { BehaviorSubject, map, Observable } from 'rxjs';

import api from './api';
import { FeatureFlag } from './models/feature-flag';

const featureFlagSubject: BehaviorSubject<FeatureFlag[]> = new BehaviorSubject(
  [],
);

export const loadFeatureFlags = (
  agencyId: string,
  workspaceId?: string | null,
  userId?: string | null,
): void => {
  if (!agencyId) {
    console.error('Missing agencyId while loading feature flags.');
    throw new Error('Missing agencyId when loading feature flags.');
  }

  const queryParams = new URLSearchParams();
  if (workspaceId) queryParams.append('workspaceId', workspaceId);
  if (userId) queryParams.append('userId', userId);

  api
    .get(`/api/v1/feature-flags?${queryParams.toString()}`, {
      headers: { 'x-agency-id': agencyId },
    })
    .then(response => featureFlagSubject.next(response?.data || []))
    .catch(error => {
      console.error('Error getting feature flags: ', error);
      throw error;
    });
};

/**
 * Returns an observable that emits whether a feature flag with a given name is enabled. This
 * observable will emit updates whenever the `loadFeatureFlags` method retrieves flags.
 *
 * Usage:
 * ```js
 * getFeatureFlagObservable('EXAMPLE_FEATURE_FLAG')
 *  .subscribe(enabled => {
 *    console.log(`Feature flag enabled: ${enabled}`)
 *  });
 *  ```
 * @param featureFlagName The feature flag to observe.
 * @returns An observable that emits whether the feature flag is enabled. Defaults to false if the flag is not found.
 */
export const getFeatureFlagObservable = (
  featureFlagName: string,
): Observable<boolean> => {
  return featureFlagSubject.pipe(
    map(flags => {
      const matchingFlag = flags.find(flag => flag.name === featureFlagName);
      return matchingFlag?.enabled || false;
    }),
  );
};
